import { appConfig } from 'appConfig';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { IMPERSONATE_SESSION_KEY } from './constants';
import { getItem } from './localstorage';

type SegmentUser = Record<string, any> | null | undefined;

/**
 * Derive user_role from permissions.
 * Homeowner and crew users get reserved system values; everyone else
 * falls back to their title or first workspace role.
 */
const resolveUserRole = (user: SegmentUser): string | undefined => {
  if (user?.permissions?.is_customer) return 'homeowner';
  if (user?.permissions?.is_crew) return 'crew';
  return (
    user?.title || (Array.isArray(user?.roles) ? user?.roles?.[0] : undefined)
  );
};

const VALID_PLAN_STATUSES = new Set(['active', 'trial', 'expired', 'future']);

let analytics: AnalyticsBrowser | null = null;

/**
 * True while an admin/support session is acting as another user.
 * Checks storage and the `ninja_sid` query param so Segment stays off before
 * AuthContext persists the impersonation token (SegmentPageTracker runs first).
 */
export const isImpersonatingSession = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  if (getItem(IMPERSONATE_SESSION_KEY)) {
    return true;
  }
  try {
    return Boolean(
      new URLSearchParams(window.location.search).get('ninja_sid')
    );
  } catch {
    return false;
  }
};

const ensureAnalytics = (
  opts: { allowDuringImpersonation?: boolean } = {}
): AnalyticsBrowser | null => {
  const writeKey = appConfig.segmentWriteKey;
  if (!writeKey) {
    return null;
  }
  if (!opts.allowDuringImpersonation && isImpersonatingSession()) {
    return null;
  }
  if (!analytics) {
    analytics = AnalyticsBrowser.load({ writeKey });
  }
  return analytics;
};

// analytics-next has no global flush; track()/group() each return a thenable
// that resolves once the event has been delivered. We track those so callers
// can await delivery via `flushSegment` right before a full-page navigation.
const pendingDeliveries = new Set<Promise<unknown>>();

// Backstop so a delivery entry is ALWAYS removed even if the underlying Segment
// thenable somehow never settles — `pendingDeliveries` can never grow unbounded.
const DELIVERY_TIMEOUT_MS = 5000;

const trackDelivery = (dispatched: unknown): void => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const settled: Promise<void> = Promise.race([
    // `.catch` makes this never reject, so no unhandled rejections.
    Promise.resolve(dispatched as Promise<unknown>).catch(() => undefined),
    new Promise<void>((resolve) => {
      timer = setTimeout(resolve, DELIVERY_TIMEOUT_MS);
    }),
  ]).then(() => {
    clearTimeout(timer);
    pendingDeliveries.delete(settled);
  });
  pendingDeliveries.add(settled);
};

/**
 * Awaits delivery of any queued Segment events. Call this ONLY right before a
 * full-page navigation (a `window.location` change), so in-flight
 * group()/track() requests aren't aborted mid-flight. A short timeout guards
 * against a stalled network so navigation is never blocked for long.
 */
export const flushSegment = async (timeoutMs = 1000): Promise<void> => {
  if (pendingDeliveries.size === 0) {
    return;
  }
  const all = Promise.all(Array.from(pendingDeliveries)).then(() => undefined);
  await Promise.race([
    all,
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
};

const getUserAgent = (): string => {
  if (typeof navigator === 'undefined' || !navigator.userAgent) {
    return '';
  }
  return navigator.userAgent;
};

const detectDeviceType = (
  userAgent: string
): 'mobile' | 'desktop' | 'unknown' => {
  if (!userAgent) {
    return 'unknown';
  }

  const mobilePatterns = [
    /Mobile/i,
    /Android/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
    /BlackBerry/i,
    /Windows Phone/i,
    /webOS/i,
  ];

  return mobilePatterns.some((regex) => regex.test(userAgent))
    ? 'mobile'
    : 'desktop';
};

const detectPlatform = (
  userAgent: string
): 'web' | 'mobile_ios' | 'mobile_android' | 'unknown' | 'unknown browser' => {
  if (!userAgent) {
    return 'unknown';
  }

  if (/iPhone|iPad|iPod|iOS/i.test(userAgent)) {
    return 'mobile_ios';
  }

  if (/Android/i.test(userAgent)) {
    return 'mobile_android';
  }

  const browserPatterns = [
    /Chrome\//i,
    /Safari\//i,
    /Firefox\//i,
    /Edge\//i,
    /Opera\//i,
    /WebKit\//i,
    /Gecko\//i,
    /Trident\//i,
    /Presto\//i,
    /CriOS\//i,
    /FxiOS\//i,
    /Version\//i,
    /SamsungBrowser\//i,
  ];

  if (browserPatterns.some((regex) => regex.test(userAgent))) {
    return 'web';
  }

  return 'unknown browser';
};

const detectPlatformDetailed = (userAgent: string): string => {
  if (!userAgent) {
    return 'unknown';
  }

  const normalized = userAgent.toLowerCase();
  const platform = detectPlatform(userAgent);

  if (platform === 'mobile_ios') {
    return 'rooflink_ios_app';
  }

  if (platform === 'mobile_android') {
    return 'rooflink_android_app';
  }

  if (normalized.includes('samsungbrowser')) {
    return 'samsung_browser';
  }

  if (normalized.includes('edge') || normalized.includes('edg/')) {
    return 'edge';
  }

  if (normalized.includes('chrome') || normalized.includes('crios')) {
    return 'chrome';
  }

  if (normalized.includes('firefox') || normalized.includes('fxios')) {
    return 'firefox';
  }

  if (normalized.includes('safari')) {
    return 'safari';
  }

  if (normalized.includes('opera') || normalized.includes('opr/')) {
    return 'opera';
  }

  return 'unknown_browser';
};

const cleanObject = (
  value: Record<string, any>,
  options: { preserveNulls?: boolean } = {}
) => {
  const { preserveNulls = false } = options;
  Object.keys(value).forEach((key) => {
    const current = value[key];
    const isEmptyString = typeof current === 'string' && current.trim() === '';
    const isEmptyObject =
      current &&
      typeof current === 'object' &&
      !Array.isArray(current) &&
      Object.keys(current).length === 0;
    const isNullish =
      current === undefined || (current === null && !preserveNulls);
    if (isNullish || isEmptyString || isEmptyObject) {
      delete value[key];
    }
  });

  return value;
};

const buildTraits = (user: SegmentUser): Record<string, any> => {
  if (!user) {
    return {};
  }

  const nameSegments = [user.first_name, user.last_name].filter(
    Boolean
  ) as string[];
  const fullName =
    nameSegments.length > 0
      ? nameSegments.join(' ')
      : user.name || user.username;

  const traits: Record<string, any> = {
    product_type: 'Rooflink',
  };

  // User identification
  if (user.id != null) {
    traits.user_id = String(user.id);
  }
  if (user.email) {
    traits.email = user.email;
  }
  if (user.first_name) {
    traits.first_name = user.first_name;
  }
  if (user.last_name) {
    traits.last_name = user.last_name;
  }
  if (fullName) {
    traits.name = fullName;
  }
  if (user.phone) {
    traits.phone = user.phone;
  }

  // User role — homeowner and crew are reserved system values that take
  // priority over workspace-configured roles.
  const userRole = resolveUserRole(user);
  if (userRole) {
    traits.user_role = userRole;
  }

  // Company information
  const company = user.primary_company || user.company;
  if (company?.name) {
    traits.workspace_company_name = company.name;
  }
  if (company?.id != null) {
    traits.workspace_company_id = String(company.id);
  }

  // Address fields (if available in user data)
  if (user.address) {
    traits.address = user.address;
  }
  if (user.city) {
    traits.city = user.city;
  }
  if (user.state) {
    traits.state = user.state;
  }
  if (user.zipcode || user.zip) {
    traits.zipcode = user.zipcode || user.zip;
  }

  // Subscription/plan information
  const subscription = user?.subscription;
  const subscriptionType = subscription?.type || subscription?.plan;

  if (subscriptionType) {
    traits.workspace_plan_type = subscriptionType === 'Pro' ? 'paid' : 'free';
    traits.workspace_plan_name = `RoofLink ${subscriptionType}`;
  }

  if (subscription?.billing_amount != null) {
    const billingAmount = Number(subscription.billing_amount);
    if (billingAmount > 0 && traits.workspace_plan_type === 'paid') {
      traits.plan_billing_amount = billingAmount;
    }
  }

  const rawStatus = subscription?.status?.toLowerCase();
  if (rawStatus && VALID_PLAN_STATUSES.has(rawStatus)) {
    traits.plan_status = rawStatus;
  }

  if (subscription?.start_date) {
    traits.workspace_plan_start_date = subscription.start_date;
  }

  if (subscription?.end_date) {
    traits.workspace_plan_end_date = subscription.end_date;
  }

  // Workspace payment added
  if (subscription?.billing_amount != null) {
    traits.workspace_payment_added = Number(subscription.billing_amount) > 0;
  } else if (rawStatus) {
    traits.workspace_payment_added = rawStatus === 'active';
  }

  // Admin email (if available)
  if (company?.admin_email || user.admin_email) {
    traits.admin_email_address = company?.admin_email || user.admin_email;
  }

  // Timezone (if available)
  if (user.timezone || company?.timezone) {
    traits.timezone = user.timezone || company?.timezone;
  }

  // TODO: Wire to real auth0_id once available on the user object
  // traits.platform_user_id = user.auth0_id;

  return cleanObject(traits);
};

const buildLoginProperties = (user: SegmentUser): Record<string, any> => {
  const userAgent = getUserAgent();
  const properties: Record<string, any> = {
    product_type: 'Rooflink',
    platform: detectPlatform(userAgent),
    platform_detailed: detectPlatformDetailed(userAgent),
  };

  if (!user) {
    return properties;
  }

  // Required: email
  if (user.email) {
    properties.email = user.email;
  }

  const userRole = resolveUserRole(user);
  if (userRole) {
    properties.user_role = userRole;
  }

  if (user.first_name) {
    properties.first_name = user.first_name;
  }
  if (user.last_name) {
    properties.last_name = user.last_name;
  }

  properties.device_type = detectDeviceType(userAgent);

  if (user.show_onboarding_steps !== undefined) {
    properties.first_login = Boolean(user.show_onboarding_steps);
  }

  const company = user.primary_company || user.company;
  if (company?.name) {
    properties.workspace_company_name = company.name;
  }

  // Subscription/plan properties
  const subscription = user?.subscription;
  const subscriptionType = subscription?.type || subscription?.plan;

  if (subscriptionType) {
    properties.workspace_plan_name = `RoofLink ${subscriptionType}`;
    properties.workspace_plan_type =
      subscriptionType === 'Pro' ? 'paid' : 'free';
  }

  const loginStatus = subscription?.status?.toLowerCase();
  if (loginStatus) {
    properties.workspace_payment_added = loginStatus === 'active';
  }

  if (subscription?.start_date) {
    properties.workspace_plan_start_date = subscription.start_date;
  }

  if (subscription?.end_date) {
    properties.workspace_plan_end_date = subscription.end_date;
  }

  return cleanObject(properties);
};

/**
 * Session identify key for tracking if identify was already sent in this session.
 * Uses sessionStorage to ensure it's sent once per browser session.
 */
const SESSION_IDENTIFIED_KEY = 'segment_session_identified';

/**
 * Build the canonical set of group-level traits sent with Segment group() calls.
 */
const buildGroupTraits = (user: SegmentUser): Record<string, any> => {
  const company = user?.primary_company || user?.company;
  const subscription = user?.subscription;
  const subscriptionType = subscription?.type || subscription?.plan;

  const traits: Record<string, any> = {
    product_type: 'Rooflink',
  };

  if (company?.name) {
    traits.company_name = company.name;
  }
  if (company?.is_active !== undefined) {
    traits.is_active = company.is_active;
  }
  if (company?.number_of_seats != null) {
    traits.number_of_seats = company.number_of_seats;
  }

  if (subscriptionType) {
    traits.workspace_plan_type = subscriptionType === 'Pro' ? 'paid' : 'free';
    traits.workspace_plan_name = `RoofLink ${subscriptionType}`;
  }

  if (subscription?.billing_amount != null) {
    const billingAmount = Number(subscription.billing_amount);
    if (billingAmount > 0 && traits.workspace_plan_type === 'paid') {
      traits.plan_billing_amount = billingAmount;
    }
  }

  const rawStatus = subscription?.status?.toLowerCase();
  if (rawStatus && VALID_PLAN_STATUSES.has(rawStatus)) {
    traits.plan_status = rawStatus;
  }

  if (subscription?.start_date) {
    traits.workspace_plan_start_date = subscription.start_date;
  }
  if (subscription?.end_date) {
    traits.workspace_plan_end_date = subscription.end_date;
  }

  return cleanObject(traits);
};

/**
 * Sends a Segment group() call for account-level targeting.
 */
const sendGroupCall = (client: AnalyticsBrowser, user: SegmentUser): void => {
  const company = user?.primary_company || user?.company;
  if (!company?.id) {
    return;
  }
  client.group(String(company.id), buildGroupTraits(user));
};

/**
 * Public wrapper to fire a GROUP call outside of the session-identify flow.
 * Use on plan changes, workspace profile updates, or any group-trait mutation.
 */
export const sendGroupUpdate = (user: SegmentUser): boolean => {
  const client = ensureAnalytics();
  if (!client || !user) {
    return false;
  }
  sendGroupCall(client, user);
  return true;
};

/**
 * Sends a GROUP call with arbitrary traits for a given workspace.
 * Segment merges traits server-side, so only send what changed.
 */
export const sendGroupTraitsUpdate = (
  workspaceId: string | number,
  traits: Record<string, any>
): boolean => {
  const client = ensureAnalytics();
  if (!client || !workspaceId) {
    return false;
  }

  trackDelivery(client.group(String(workspaceId), cleanObject(traits)));
  return true;
};

export const sendSignedInEvent = (user: SegmentUser): boolean => {
  const client = ensureAnalytics();
  if (!client || !user || user.id == null) {
    return false;
  }

  const properties = buildLoginProperties(user);

  // Segment Identify + Group + set session flag
  sendSessionIdentify(user);
  client.track('Signed In', properties);

  return true;
};

/**
 * Sends an identify call with full user traits on the first logged-in pageview of each session.
 * This ensures Segment destinations can properly target and personalize
 * experiences even for users who remain logged in between sessions.
 *
 * Also sends a group() call for company-level targeting in Appcues.
 *
 * @param user - The user object from /api/light/auth/me/
 * @returns True if identify was sent, false if already sent this session or no analytics
 */
export const sendSessionIdentify = (
  user: SegmentUser,
  opts: { allowDuringImpersonation?: boolean } = {}
): boolean => {
  const client = ensureAnalytics(opts);
  if (!client || !user || user.id == null) {
    return false;
  }

  // Check if already identified this session
  try {
    if (sessionStorage.getItem(SESSION_IDENTIFIED_KEY) === String(user.id)) {
      return false;
    }
  } catch {
    // sessionStorage not available (e.g., private browsing), continue anyway
  }

  const userId = String(user.id);
  const traits = buildTraits(user);

  client.identify(userId, traits);
  // Skip the group() call when acting as a homeowner during impersonation so
  // the rep's company/session is not polluted with homeowner group traits.
  if (!opts.allowDuringImpersonation) {
    sendGroupCall(client, user);
  }

  // Mark as identified for this session
  try {
    sessionStorage.setItem(SESSION_IDENTIFIED_KEY, userId);
  } catch {
    // sessionStorage not available, continue without marking
  }

  return true;
};

/**
 * Clears the session identify flag. Call this on logout to ensure
 * identify is sent again on next login.
 */
export const clearSessionIdentify = (): void => {
  try {
    sessionStorage.removeItem(SESSION_IDENTIFIED_KEY);
  } catch {
    // sessionStorage not available
  }
};

export const sendPageView = (
  name?: string,
  properties?: Record<string, any>
): boolean => {
  const client = ensureAnalytics();
  if (!client) {
    return false;
  }

  if (name) {
    client.page(name, properties);
  } else {
    client.page(properties);
  }
  return true;
};

export const isSegmentAvailable = (): boolean => ensureAnalytics() !== null;

/**
 * Eagerly initializes Segment and assigns it to window.analytics
 * so PostHog can use device-mode integration.
 * Must be called before PostHog initializes.
 */
export const initSegmentForPostHog = (): void => {
  const client = ensureAnalytics();
  if (client) {
    (window as any).analytics = client;
  } else {
    (window as any).analytics = undefined;
  }
};

export const resetSegment = () => {
  if (analytics) {
    analytics.reset();
  }
};

/**
 * Sends a custom track event to Segment.
 * @param eventName - The name of the event to track
 * @param properties - Optional properties to send with the event
 * @param user - Optional user object for traits enrichment
 * @param options - Optional behavior flags. Set `preserveNullProperties: true`
 *   when the event's downstream contract requires explicit `null` values
 *   (e.g. nullable fields that must remain queryable in PostHog).
 * @returns True if the event was sent, false otherwise
 */
export const trackEvent = (
  eventName: string,
  properties?: Record<string, any>,
  user?: SegmentUser, // this is the auth context user object
  options: {
    preserveNullProperties?: boolean;
    /**
     * Set to `true` only for the two homeowner signing events
     * (`Homeowner Document Viewed` / `Homeowner Document Signed`) so they fire
     * even during a Customer Sign Now impersonation session. All other calls
     * remain suppressed.
     */
    allowDuringImpersonation?: boolean;
  } = {}
): boolean => {
  const userAgent = getUserAgent();
  const traits = user ? buildTraits(user) : {};

  const enrichedProperties = cleanObject(
    {
      ...properties,
      ...traits,
      product_type: 'Rooflink',
      platform: detectPlatform(userAgent),
      platform_detailed: detectPlatformDetailed(userAgent),
      device_type: detectDeviceType(userAgent),
    },
    { preserveNulls: options.preserveNullProperties }
  );

  const client = ensureAnalytics({
    allowDuringImpersonation: options.allowDuringImpersonation,
  });
  if (!client) {
    return false;
  }

  if (user) {
    sendSessionIdentify(user, {
      allowDuringImpersonation: options.allowDuringImpersonation,
    });
  }

  trackDelivery(client.track(eventName, enrichedProperties));
  return true;
};
